"use client";

import { Plus, Trash2 } from "lucide-react";
import type { ResumeDocument } from "@/types/cv";
import { createEmptyExperience } from "@/lib/cv-builder/resume-factory";
import { CV_BUILDER_EXAMPLE } from "@/components/cv-builder/sample-resume";
import {
  TextField,
  MonthField,
  BulletList,
} from "@/components/cv-builder/fields";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface StepProps {
  resume: ResumeDocument;
  onChange: (next: ResumeDocument) => void;
}

export function ExperienceStep({ resume, onChange }: StepProps) {
  const entries = resume.working_experience;

  const patch = (
    index: number,
    value: ResumeDocument["working_experience"][number],
  ) =>
    onChange({
      ...resume,
      working_experience: entries.map((entry, i) =>
        i === index ? value : entry,
      ),
    });

  const remove = (index: number) =>
    onChange({
      ...resume,
      working_experience: entries.filter((_, i) => i !== index),
    });

  const add = () =>
    onChange({
      ...resume,
      working_experience: [...entries, createEmptyExperience()],
    });

  return (
    <div className="space-y-5">
      {entries.map((entry, index) => (
        <div
          key={index}
          className="space-y-4 rounded-xl border border-border bg-card p-4"
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-muted-foreground">
              Pengalaman {index + 1}
            </p>
            {entries.length > 1 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => remove(index)}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Hapus
              </Button>
            ) : null}
          </div>
          <TextField
            label="Posisi / jabatan"
            value={entry.title}
            onChange={(title) => patch(index, { ...entry, title })}
            placeholder={CV_BUILDER_EXAMPLE.experienceTitle}
          />
          <TextField
            label="Perusahaan"
            value={entry.company}
            onChange={(company) => patch(index, { ...entry, company })}
            placeholder={CV_BUILDER_EXAMPLE.experienceCompany}
          />
          <div className="grid grid-cols-2 gap-3">
            <MonthField
              label="Mulai"
              value={entry.start_date}
              onChange={(start_date) => patch(index, { ...entry, start_date })}
            />
            <MonthField
              label="Selesai"
              value={entry.end_date}
              disabled={entry.is_current}
              onChange={(end_date) => patch(index, { ...entry, end_date })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`current-${index}`}
              checked={entry.is_current}
              onCheckedChange={(checked) =>
                patch(index, {
                  ...entry,
                  is_current: checked === true,
                  end_date: checked === true ? null : entry.end_date,
                })
              }
            />
            <Label htmlFor={`current-${index}`} className="text-sm font-normal">
              Masih bekerja di sini
            </Label>
          </div>
          <BulletList
            label="Apa yang kamu kerjakan"
            helper="Satu poin per baris. Mulai dengan kata kerja, sebut hasilnya kalau ada angka."
            points={entry.points}
            onChange={(points) => patch(index, { ...entry, points })}
            addLabel="Tambah poin"
            placeholder={CV_BUILDER_EXAMPLE.experienceBullet}
          />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} className="w-full">
        <Plus className="mr-1.5 h-4 w-4" />
        Tambah pengalaman
      </Button>
    </div>
  );
}
